import { useEffect, useState } from 'react';
import { Activity, Loader2, Settings, Tag, User as UserIcon } from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';

/**
 * Timeline historique fusionnée (US-BAT-07) : changes de statut +
 * affectations service/responsable, triés par date décroissante.
 */

interface HistoriqueItem {
  kind: 'statut' | 'affectation';
  id: string;
  date: string;
  payload: Record<string, unknown>;
}

export function HistoriqueTimeline({
  entityType,
  entityId,
}: {
  entityType: 'batiment' | 'etage' | 'salle' | 'service' | 'equipement';
  entityId: string;
}) {
  const [items, setItems] = useState<HistoriqueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const endpoint =
          entityType === 'batiment'
            ? `/batiments/${entityId}/historique`
            : entityType === 'etage'
              ? `/etages/${entityId}/historique`
              : entityType === 'salle'
                ? `/salles/${entityId}/historique`
                : entityType === 'service'
                  ? `/services/${entityId}/historique`
                  : `/equipements/${entityId}/historique`;
        const res = await api.get<{ data: HistoriqueItem[] }>(endpoint);
        setItems(res.data.data);
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [entityType, entityId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  if (errorMessage) {
    return <p className="text-sm text-red-600">{errorMessage}</p>;
  }

  if (items.length === 0) {
    return <p className="text-sm text-gray-500 italic">Aucun événement enregistré.</p>;
  }

  return (
    <ol className="relative border-l border-gray-200 ml-3 space-y-4">
      {items.map((item) => (
        <li key={`${item.kind}-${item.id}`} className="ml-5">
          <span
            className={`absolute -left-2.5 flex items-center justify-center w-5 h-5 rounded-full ring-4 ring-white ${
              item.kind === 'statut' ? 'bg-blue-100' : 'bg-primary-100'
            }`}
          >
            {item.kind === 'statut' ? (
              <Tag className="w-3 h-3 text-blue-700" />
            ) : (
              <Settings className="w-3 h-3 text-primary-700" />
            )}
          </span>
          <div className="rounded-lg border border-gray-200 p-2.5 bg-white text-xs">
            <p className="text-gray-700">
              {item.kind === 'statut' ? (
                <>
                  <span className="font-semibold">Statut</span> :{' '}
                  <code className="bg-gray-100 px-1 rounded">
                    {String(item.payload.ancien_statut)}
                  </code>{' '}
                  →{' '}
                  <code className="bg-blue-50 text-blue-700 px-1 rounded">
                    {String(item.payload.nouveau_statut)}
                  </code>
                </>
              ) : (
                <>
                  <span className="font-semibold">Affectation</span>
                  {item.payload.service && (
                    <>
                      {' '}
                      → service <span className="font-medium">{String(item.payload.service)}</span>
                    </>
                  )}
                  {item.payload.responsable && (
                    <>
                      {' '}
                      · responsable{' '}
                      <span className="font-medium">{String(item.payload.responsable)}</span>
                    </>
                  )}
                  {item.payload.type_utilisation && (
                    <span className="text-gray-500">
                      {' '}
                      ({String(item.payload.type_utilisation)})
                    </span>
                  )}
                </>
              )}
            </p>
            <p className="text-gray-500 mt-1 flex items-center">
              <Activity className="w-3 h-3 mr-1" />
              {new Date(item.date).toLocaleString()}
              {item.payload.utilisateur ? (
                <span className="ml-2 inline-flex items-center">
                  <UserIcon className="w-3 h-3 mr-1" />
                  {String(item.payload.utilisateur as string)}
                </span>
              ) : null}
            </p>
            {Boolean(item.payload.motif) && (
              <p className="text-gray-600 mt-1 italic">"{String(item.payload.motif)}"</p>
            )}
            {Boolean(item.payload.commentaire) && (
              <p className="text-gray-600 mt-1">{String(item.payload.commentaire)}</p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
